import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { TRAINING_URL } from "../shared/src/lib/shared";
import { ErrorHandlerService } from "./error-handler.service";

export interface CreateTrainingRequest {
    title: string;
    date: string; // ISO date string
    trainingTypeId: number;
    deliveryMethodId: number;
    durationId: number;
    startTime?: string;
    endTime?: string;
    expectedAttendees?: number;
    isRandomize?: boolean;
    trainerIds?: number[];
    categoryIds?: number[];
    subCategoryIds?: number[];
    quizQuestions: {
        1: {
            questions: number[];
            endDate: string;
            endTime?: string;
            durationId: number;
        };
        2: {
            questions: number[];
            endDate: string;
            endTime?: string;
            durationId: number;
        };
    };
}

@Injectable({
    providedIn: 'root',
})
export class TrainingSummaryService {

    private readonly TRAINING_SUMMARY_URL = `${TRAINING_URL}`;

    constructor(
        private http: HttpClient,
        private errorHandler: ErrorHandlerService
    ) { }

    //get all delivery methods
    getAllDeliveryMethods(searchTerm?: string, isGroupTraining: boolean = false): Observable<any> {
        let params = new HttpParams();
        if (searchTerm) {
            params = params.set('search', searchTerm);
        }
        if (isGroupTraining) {
            params = params.set('isGroupTraining', isGroupTraining);
        }
        return this.http.get(`${this.TRAINING_SUMMARY_URL}/trainingDeliveryMethod/getAll`, { params });
    }

    //get all Audience types
    getAllAudienceTypes(searchTerm?: string): Observable<any> {
        let params = new HttpParams();
        if (searchTerm) {
            params = params.set('search', searchTerm);
        }
        return this.http.get(`${this.TRAINING_SUMMARY_URL}/trainingAudienceType/getAll`, { params });
    }

    //create training
    createTraining(trainingData: CreateTrainingRequest, countryId?: number): Observable<any> {
        let params = new HttpParams();
        if (countryId) {
            params = params.set('countryId', countryId.toString());
        }
        return this.http.post(`${this.TRAINING_SUMMARY_URL}/training/save`, trainingData, { params });
    }

    //get training events for calendar
    getTrainingEvents(countryId?: number, trainingTypeId?: number): Observable<any> {
        let params = new HttpParams();
        if (countryId != null && countryId !== undefined) {
            params = params.set('countryId', countryId.toString());
        }
        if (trainingTypeId != null && trainingTypeId !== undefined) {
            params = params.set('trainingTypeId', trainingTypeId.toString());
        }
        return this.http.get(`${this.TRAINING_SUMMARY_URL}/training/getTrainingEvents`, { params });
    }

    //update basic training fields only
    updateBasicTraining(trainingData: any): Observable<any> {
        return this.http.put(`${this.TRAINING_SUMMARY_URL}/training/updateTrainingEvents`, trainingData);
    }

    //delete training
    deleteTraining(id: number, countryId?: number): Observable<any> {
        let params = new HttpParams();
        if (countryId != null && countryId !== undefined) {
            params = params.set('countryId', countryId.toString());
        }
        return this.http.delete(`${this.TRAINING_SUMMARY_URL}/training/delete/${id}`, { params });
    }

    //cancel training
    cancelTraining(id: number): Observable<any> {
        return this.http.post(`${this.TRAINING_SUMMARY_URL}/training/cancel/${id}`, {});
    }

    //get training list from view
    getTrainingList(filters?: {
        trainingTypeIds?: number[];
        durationIds?: number[];
        deliveryMethodIds?: number[];
        statusIds?: number[];
        trainerIds?: number[];
        startDate?: string;
        endDate?: string;
        months?: number[];
        search?: string;
        countryIds?: number[];
        moduleIds?: number[];
        formatToolIds?: number[];
    }): Observable<any> {
        let params = new HttpParams();

        if (filters) {
            if (filters.trainingTypeIds && filters.trainingTypeIds.length > 0) {
                params = params.set('trainingTypeIds', filters.trainingTypeIds.join(','));
            }

            if (filters.durationIds && filters.durationIds.length > 0) {
                params = params.set('durationIds', filters.durationIds.join(','));
            }

            if (filters.deliveryMethodIds && filters.deliveryMethodIds.length > 0) {
                params = params.set('deliveryMethodIds', filters.deliveryMethodIds.join(','));
            }

            if (filters.statusIds && filters.statusIds.length > 0) {
                console.log('Sending status IDs to API:', filters.statusIds);
                params = params.set('statusIds', filters.statusIds.join(','));
            }

            if (filters.trainerIds && filters.trainerIds.length > 0) {
                console.log('Sending trainer IDs to API:', filters.trainerIds);
                params = params.set('trainerIds', filters.trainerIds.join(','));
            }

            if (filters.startDate) {
                params = params.set('startDate', filters.startDate);
            }

            if (filters.endDate) {
                params = params.set('endDate', filters.endDate);
            }

            if (filters.months && filters.months.length > 0) {
                params = params.set('months', filters.months.join(','));
            }

            if (filters.countryIds && filters.countryIds.length > 0) {
                params = params.set('countryIds', filters.countryIds.join(','));
            }

            if (filters.moduleIds && filters.moduleIds.length > 0) {
                params = params.set('moduleIds', filters.moduleIds.join(','));
            }

            if (filters.formatToolIds && filters.formatToolIds.length > 0) {
                params = params.set('formatToolIds', filters.formatToolIds.join(','));
            }

            if (filters.search && filters.search.trim() !== '') {
                console.log('Sending search parameter to API:', filters.search);
                params = params.set('search', filters.search.trim());
            }
        }

        return this.http.get(`${this.TRAINING_SUMMARY_URL}/training/trainingList`, { params });
    }

    //export training list to Excel
    exportTrainingList(filters?: {
        trainingTypeIds?: number[];
        durationIds?: number[];
        deliveryMethodIds?: number[];
        statusIds?: number[];
        trainerIds?: number[];
        startDate?: string;
        endDate?: string;
        months?: number[];
        search?: string;
        countryIds?: number[];
        moduleIds?: number[];
        formatToolIds?: number[];
    }): Observable<Blob> {
        const payload = {
            ...filters
        };

        return this.http.post<Blob>(
            `${this.TRAINING_SUMMARY_URL}/training/downloadExcelReport`,
            payload,
            { responseType: 'blob' as 'json' }
        );
    }

    //get trainers list for dropdown using training service API
    getTrainersList(searchTerm?: string, countryId?: number): Observable<any> {
        let params = new HttpParams();
        if (searchTerm && searchTerm.trim() !== '') {
            params = params.set('search', searchTerm.trim());
        }
        if (countryId) {
            params = params.set('countryId', countryId.toString());
        }
        return this.http.get(`${this.TRAINING_SUMMARY_URL}/training/trainers`, { params });
    }

    //get training by id
    getTrainingById(id: number): Observable<any> {
        return this.http.get(`${this.TRAINING_SUMMARY_URL}/training/getTrainingById/${id}`);
    }

    //update training
    updateTraining(trainingData: any, countryId?: number): Observable<any> {
        let params = new HttpParams();
        if (countryId) {
            params = params.set('countryId', countryId.toString());
        }
        return this.http.post(`${this.TRAINING_SUMMARY_URL}/training/updateTraining`, trainingData, { params });
    }

    //get training attendance list
    getTrainingAttendanceList(filters?: {
        pageIndex?: number;
        pageSize?: number;
        search?: string;
        countryIds?: number[];
        fromDate?: string;
        toDate?: string;
        deliveryMethodIds?: number[];
        durationIds?: number[];
        trainingTypeIds?: number[];
        promoterCategoryIds?: number[];
        months?: number[];
    }): Observable<any> {
        let params = new HttpParams();
        if (filters) {
            if (filters.pageIndex) {
                params = params.set('pageIndex', filters.pageIndex);
            }
            if (filters.pageSize) {
                params = params.set('pageSize', filters.pageSize);
            }
            if (filters.search) {
                params = params.set('search', filters.search);
            }
            if (filters.countryIds && filters.countryIds.length > 0) {
                params = params.set('countryIds', filters.countryIds.join(','));
            }
            if (filters.fromDate) {
                params = params.set('fromDate', filters.fromDate);
            }
            if (filters.toDate) {
                params = params.set('toDate', filters.toDate);
            }
            if (filters.deliveryMethodIds && filters.deliveryMethodIds.length > 0) {
                params = params.set('deliveryMethodIds', filters.deliveryMethodIds.join(','));
            }
            if (filters.durationIds && filters.durationIds.length > 0) {
                params = params.set('durationIds', filters.durationIds.join(','));
            }
            if (filters.trainingTypeIds && filters.trainingTypeIds.length > 0) {
                params = params.set('trainingTypeIds', filters.trainingTypeIds.join(','));
            }
            if (filters.promoterCategoryIds && filters.promoterCategoryIds.length > 0) {
                params = params.set('promoterCategoryIds', filters.promoterCategoryIds.join(','));
            }
            if (filters.months && filters.months.length > 0) {
                params = params.set('months', filters.months.join(','));
            }
        }
        return this.http.get(`${this.TRAINING_SUMMARY_URL}/training/getTrainingAttendanceList`, { params });
    }

    getGroupTrainingAttendanceSummary(filters?: {
        pageIndex?: number;
        pageSize?: number;
        search?: string;
        countryIds?: number[];
        fromDate?: string;
        toDate?: string;
        deliveryMethodIds?: number[];
        durationIds?: number[];
        trainingTypeIds?: number[];
        moduleIds?: number[];
    }): Observable<any> {
        let params = new HttpParams();
        if (filters) {
            if (filters.pageIndex) {
                params = params.set('pageIndex', filters.pageIndex);
            }
            if (filters.pageSize) {
                params = params.set('pageSize', filters.pageSize);
            }
            if (filters.search) {
                params = params.set('search', filters.search);
            }
            if (filters.countryIds && filters.countryIds.length > 0) {
                params = params.set('countryIds', filters.countryIds.join(','));
            }
            if (filters.fromDate) {
                params = params.set('fromDate', filters.fromDate);
            }
            if (filters.toDate) {
                params = params.set('toDate', filters.toDate);
            }
            if (filters.deliveryMethodIds && filters.deliveryMethodIds.length > 0) {
                params = params.set('deliveryMethodIds', filters.deliveryMethodIds.join(','));
            }
            if (filters.durationIds && filters.durationIds.length > 0) {
                params = params.set('durationIds', filters.durationIds.join(','));
            }
            if (filters.trainingTypeIds && filters.trainingTypeIds.length > 0) {
                params = params.set('trainingTypeIds', filters.trainingTypeIds.join(','));
            }
            if (filters.moduleIds && filters.moduleIds.length > 0) {
                params = params.set('moduleIds', filters.moduleIds.join(','));
            }
        }
        return this.http.get(`${this.TRAINING_SUMMARY_URL}/training/getGroupTrainingAttendanceSummary`, { params });
    }

    getGroupTrainingPromoterDetails(filters?: {
        pageIndex?: number;
        pageSize?: number;
        search?: string;
        countryIds?: number[];
        fromDate?: string;
        toDate?: string;
        deliveryMethodIds?: number[];
        durationIds?: number[];
        trainingTypeIds?: number[];
        moduleIds?: number[];
        trainingStatus?: string;
        promoterCategoryIds?: number[];
    }): Observable<any> {
        let params = new HttpParams();
        if (filters) {
            if (filters.pageIndex) {
                params = params.set('pageIndex', filters.pageIndex);
            }
            if (filters.pageSize) {
                params = params.set('pageSize', filters.pageSize);
            }
            if (filters.search) {
                params = params.set('search', filters.search);
            }
            if (filters.countryIds && filters.countryIds.length > 0) {
                params = params.set('countryIds', filters.countryIds.join(','));
            }
            if (filters.fromDate) {
                params = params.set('fromDate', filters.fromDate);
            }
            if (filters.toDate) {
                params = params.set('toDate', filters.toDate);
            }
            if (filters.deliveryMethodIds && filters.deliveryMethodIds.length > 0) {
                params = params.set('deliveryMethodIds', filters.deliveryMethodIds.join(','));
            }
            if (filters.durationIds && filters.durationIds.length > 0) {
                params = params.set('durationIds', filters.durationIds.join(','));
            }
            if (filters.trainingTypeIds && filters.trainingTypeIds.length > 0) {
                params = params.set('trainingTypeIds', filters.trainingTypeIds.join(','));
            }
            if (filters.moduleIds && filters.moduleIds.length > 0) {
                params = params.set('moduleIds', filters.moduleIds.join(','));
            }
            if (filters.trainingStatus) {
                params = params.set('trainingStatus', filters.trainingStatus);
            }
            if (filters.promoterCategoryIds && filters.promoterCategoryIds.length > 0) {
                params = params.set('promoterCategoryIds', filters.promoterCategoryIds.join(','));
            }
        }
        return this.http.get(`${this.TRAINING_SUMMARY_URL}/training/getGroupTrainingPromoterDetails`, { params });
    }

    exportGroupTrainingAttendanceSummary(filters?: {
        search?: string;
        countryIds?: number[];
        fromDate?: string;
        toDate?: string;
        deliveryMethodIds?: number[];
        durationIds?: number[];
        trainingTypeIds?: number[];
        moduleIds?: number[];
    }): Observable<Blob> {
        const body = filters ?? {};
        return this.http.post<Blob>(
            `${this.TRAINING_SUMMARY_URL}/training/exportGroupTrainingAttendanceSummary/downloadExcelReport`,
            body,
            { responseType: 'blob' as 'json' },
        );
    }

    exportGroupTrainingPromoterDetails(filters?: {
        search?: string;
        countryIds?: number[];
        fromDate?: string;
        toDate?: string;
        deliveryMethodIds?: number[];
        durationIds?: number[];
        trainingTypeIds?: number[];
        moduleIds?: number[];
        trainingStatus?: string;
        promoterCategoryIds?: number[];
    }): Observable<Blob> {
        const body = filters ?? {};
        return this.http.post<Blob>(
            `${this.TRAINING_SUMMARY_URL}/training/exportGroupTrainingPromoterDetails/downloadExcelReport`,
            body,
            { responseType: 'blob' as 'json' },
        );
    }

    //export training attendance list to Excel (same filters as getTrainingAttendanceList)
    exportTrainingAttendanceList(filters?: {
        search?: string;
        countryIds?: number[];
        fromDate?: string;
        toDate?: string;
        deliveryMethodIds?: number[];
        durationIds?: number[];
        trainingTypeIds?: number[];
        promoterCategoryIds?: number[];
        months?: number[];
    }): Observable<Blob> {
        const body = filters ? {
            search: filters.search,
            countryIds: filters.countryIds,
            fromDate: filters.fromDate,
            toDate: filters.toDate,
            deliveryMethodIds: filters.deliveryMethodIds,
            durationIds: filters.durationIds,
            trainingTypeIds: filters.trainingTypeIds,
            promoterCategoryIds: filters.promoterCategoryIds,
            months: filters.months,
        } : {};
        return this.http.post<Blob>(
            `${this.TRAINING_SUMMARY_URL}/training/trainingAttendanceList/downloadExcelReport`,
            body,
            { responseType: 'blob' as 'json' }
        );
    }

    //get quiz details list
    getQuizDetailsList(filters?: {
        pageIndex?: number;
        pageSize?: number;
        search?: string;
        countryIds?: number[];
        quizTypeIds?: number[];
        quizSource?: string;
        trainingTypeIds?: number[];
        startDate?: string;
        endDate?: string;
    }): Observable<any> {
        let params = new HttpParams();
        if (filters) {
            if (filters.pageIndex) {
                params = params.set('pageIndex', filters.pageIndex);
            }
            if (filters.pageSize) {
                params = params.set('pageSize', filters.pageSize);
            }
            if (filters.search) {
                params = params.set('search', filters.search);
            }
            if (filters.countryIds && filters.countryIds.length > 0) {
                filters.countryIds.forEach(id => {
                    params = params.append('countryIds', id.toString());
                });
            }
            if (filters.quizTypeIds && filters.quizTypeIds.length > 0) {
                filters.quizTypeIds.forEach(id => {
                    params = params.append('quizTypeIds', id.toString());
                });
            }
            if (filters.quizSource) {
                params = params.set('quizSource', filters.quizSource);
            }
            if (filters.trainingTypeIds && filters.trainingTypeIds.length > 0) {
                filters.trainingTypeIds.forEach(id => {
                    params = params.append('trainingTypeIds', id.toString());
                });
            }
            if (filters.startDate) {
                params = params.set('startDate', filters.startDate);
            }
            if (filters.endDate) {
                params = params.set('endDate', filters.endDate);
            }
        }
        return this.http.get(`${this.TRAINING_SUMMARY_URL}/quiz-details/getQuizDetailsList`, { params });
    }

    //export quiz details list to Excel
    exportQuizDetailsList(filters?: {
        countryIds?: number[];
        quizTypeIds?: number[];
        quizSource?: string;
        trainingTypeIds?: number[];
        startDate?: string;
        endDate?: string;
        search?: string;
    }): Observable<Blob> {
        let params = new HttpParams();
        if (filters) {
            if (filters.countryIds && filters.countryIds.length > 0) {
                filters.countryIds.forEach(id => {
                    params = params.append('countryIds', id.toString());
                });
            }
            if (filters.quizTypeIds && filters.quizTypeIds.length > 0) {
                filters.quizTypeIds.forEach(id => {
                    params = params.append('quizTypeIds', id.toString());
                });
            }
            if (filters.quizSource) {
                params = params.set('quizSource', filters.quizSource);
            }
            if (filters.trainingTypeIds && filters.trainingTypeIds.length > 0) {
                filters.trainingTypeIds.forEach(id => {
                    params = params.append('trainingTypeIds', id.toString());
                });
            }
            if (filters.startDate) {
                params = params.set('startDate', filters.startDate);
            }
            if (filters.endDate) {
                params = params.set('endDate', filters.endDate);
            }
            if (filters.search) {
                params = params.set('search', filters.search);
            }
        }
        return this.http.post<Blob>(
            `${this.TRAINING_SUMMARY_URL}/quiz-details/downloadExcelReport`,
            {},
            { params, responseType: 'blob' as 'json' }
        );
    }
}
